import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Building2, Users, LogIn, ShieldCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../contexts/AuthContext';

const PageShell = ({ children }) => (
  <div
    className="min-h-screen w-full flex items-center justify-center p-4"
    style={{
      background: 'linear-gradient(160deg, #ecf6fb 0%, #d8edf7 45%, #cfe8f5 100%)',
      paddingBottom: 'calc(72px + env(safe-area-inset-bottom))',
    }}
    data-testid="login-page"
  >
    {children}
  </div>
);

const inputCls =
  'w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-800 text-[15px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0E7490] focus:border-transparent transition-all';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [forgotStep, setForgotStep] = useState('email'); // 'email' | 'otp' | 'password'
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [brandName, setBrandName] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem('plash_remember_email');
    if (saved) {
      setEmail(saved);
      setRemember(true);
    }
  }, []);

  useEffect(() => {
    const company = new URLSearchParams(window.location.search).get('company');
    if (!company) return;
    axios.get(`${API_URL}/trial/branding`, { params: { company } })
      .then(({ data }) => { if (data?.company_name) setBrandName(data.company_name); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        if (remember) localStorage.setItem('plash_remember_email', email);
        else localStorage.removeItem('plash_remember_email');
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const closeForgot = () => {
    setShowForgotPassword(false);
    setForgotStep('email');
    setResetEmail('');
    setOtp('');
    setResetToken('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/forgot-password`, { email: resetEmail });
      toast.success(data?.message || 'A 6-digit code has been emailed to you.');
      setOtp('');
      setForgotStep('otp');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not send the reset code. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/verify-otp`, { email: resetEmail, otp });
      setResetToken(data.reset_token);
      toast.success('Code verified. Choose a new password.');
      setForgotStep('password');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast.success('Password reset! You can now sign in.');
      closeForgot();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to reset password. The code may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <PageShell>
        <div className="w-full max-w-sm">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_18px_50px_-20px_rgba(8,47,73,0.35)] p-7 fade-in" data-testid="forgot-password-panel">
            <div className="flex flex-col items-center">
              <img src="/plash-logo.png" alt="Plash" className="h-16 w-auto object-contain" data-testid="plash-logo" />
              <h2 className="mt-3 text-lg font-bold text-slate-800">
                {forgotStep === 'email' && 'Reset Password'}
                {forgotStep === 'otp' && 'Enter Reset Code'}
                {forgotStep === 'password' && 'Set New Password'}
              </h2>
              <p className="text-center text-slate-500 text-sm mt-1 mb-5">
                {forgotStep === 'email' && 'Enter your office email to receive a 6-digit code'}
                {forgotStep === 'otp' && `We emailed a 6-digit code to ${resetEmail}. It expires in 10 minutes.`}
                {forgotStep === 'password' && 'Choose a new password for your account'}
              </p>
            </div>

            {forgotStep === 'email' && (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                    <Mail size={15} className="text-[#0E7490]" /> Email Address
                  </label>
                  <input
                    data-testid="reset-email-input"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    placeholder="youremail@example.com"
                    className={inputCls}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="send-otp-button"
                  disabled={loading}
                  className="w-full py-3 rounded-xl font-bold text-white bg-[#0E7490] hover:bg-[#155E75] shadow-md shadow-[#0E7490]/25 transition-all active:scale-[0.99] disabled:opacity-60"
                >
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>
              </form>
            )}

            {forgotStep === 'otp' && (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                    <ShieldCheck size={15} className="text-[#0E7490]" /> 6-Digit Code
                  </label>
                  <input
                    data-testid="otp-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    placeholder="123456"
                    className={inputCls + ' tracking-[0.5em] text-center text-lg font-bold'}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="verify-otp-button"
                  disabled={loading || otp.length !== 6}
                  className="w-full py-3 rounded-xl font-bold text-white bg-[#0E7490] hover:bg-[#155E75] shadow-md shadow-[#0E7490]/25 transition-all active:scale-[0.99] disabled:opacity-60"
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading}
                  data-testid="resend-otp-button"
                  className="w-full text-sm font-semibold text-[#0E7490] hover:text-[#155E75] transition-colors disabled:opacity-60"
                >
                  Resend code
                </button>
              </form>
            )}

            {forgotStep === 'password' && (
              <form onSubmit={handleSetNewPassword} className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                    <Lock size={15} className="text-[#0E7490]" /> New Password
                  </label>
                  <div className="relative">
                    <input
                      data-testid="new-password-input"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Enter new password"
                      className={inputCls + ' pr-11'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0E7490] transition-colors"
                      aria-label="Toggle password visibility"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5">
                    <Lock size={15} className="text-[#0E7490]" /> Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      data-testid="confirm-new-password-input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="Confirm new password"
                      className={inputCls + ' pr-11'}
                    />
                    <button
                      type="button"
                      data-testid="toggle-confirm-password-visibility"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0E7490] transition-colors"
                      aria-label="Toggle confirm password visibility"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="reset-password-submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl font-bold text-white bg-[#0E7490] hover:bg-[#155E75] shadow-md shadow-[#0E7490]/25 transition-all active:scale-[0.99] disabled:opacity-60"
                >
                  {loading ? 'Saving...' : 'Reset Password'}
                </button>
              </form>
            )}

            <button
              onClick={closeForgot}
              data-testid="back-to-login-button"
              className="mt-4 mx-auto flex items-center gap-1.5 text-sm font-semibold text-[#0E7490] hover:text-[#155E75] transition-colors"
            >
              <ArrowLeft size={15} /> Back to Login
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="w-full max-w-sm">
        <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_18px_50px_-20px_rgba(8,47,73,0.35)] p-6 sm:p-7 fade-in">
          {/* Brand */}
          <div className="flex flex-col items-center">
            <img
              src="/plash-logo.png"
              alt="Plash Swimming Pool"
              className="h-20 sm:h-24 w-auto object-contain"
              data-testid="plash-logo"
            />
            <p className="mt-2 text-[13px] font-semibold tracking-wide text-[#0E7490]">Beyond Attendance. Built for Growth</p>
            {brandName && (
              <h1 className="mt-3 text-lg font-bold text-slate-800 text-center" data-testid="brand-heading">Login to {brandName}</h1>
            )}
          </div>

          {/* Office / Member toggle */}
          <div className="mt-5 grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100">
            <button
              type="button"
              data-testid="office-login-tab"
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-bold text-white bg-[#0E7490] shadow-sm"
            >
              <Building2 size={16} /> OFFICE LOGIN
            </button>
            <button
              type="button"
              data-testid="parent-login-tab"
              onClick={() => navigate('/parent/login')}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-bold text-slate-600 hover:bg-white transition-colors"
            >
              <Users size={16} /> MEMBER LOGIN
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5" htmlFor="email">
                <Mail size={15} className="text-[#0E7490]" /> Email Address
              </label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="youremail@example.com"
                className={inputCls}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5" htmlFor="password">
                <Lock size={15} className="text-[#0E7490]" /> Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className={inputCls + ' pr-11'}
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0E7490] transition-colors"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-600 cursor-pointer select-none" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  data-testid="remember-me-checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#0E7490] focus:ring-[#0E7490]"
                />
                Remember Me
              </label>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                data-testid="forgot-password-link"
                className="text-sm font-semibold text-[#0E7490] hover:text-[#155E75] transition-colors"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white bg-[#0E7490] hover:bg-[#155E75] shadow-md shadow-[#0E7490]/25 transition-all active:scale-[0.99] disabled:opacity-60"
            >
              <LogIn size={18} /> {loading ? 'Please wait...' : 'SIGN IN'}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-[#0E7490]" /> Secure &amp; Confidential
            </span>
            <span className="text-slate-300">|</span>
            <span>Version 1.0.0</span>
          </div>

          <button
            type="button"
            onClick={() => navigate('/demo/login')}
            data-testid="request-demo-link"
            className="mt-4 w-full py-2.5 rounded-xl text-sm font-semibold text-[#0E7490] border border-[#0E7490]/30 hover:bg-[#0E7490]/5 transition-colors"
          >
            Try the Employee Attendance Tracker · Request a 7-Day Demo
          </button>
        </div>
      </div>
    </PageShell>
  );
}
